// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';

// interfaces/types
import type { ReduxMiddleware, ReduxStore, ReduxNext } from '@makemydeal/dr-shared-store';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import { scopedActions, scopedActionTypes } from '@makemydeal/shared-scoped-store';
import type { StateTree } from '@makemydeal/dr-dash-types';

// action types
import { AI_VEHICLE_RECOMMENDATIONS_SUCCESS, PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';
import { UPDATED_OFFER_PRICE_IMMEDIATE, UPDATED_DOWN_PAYMENT_IMMEDIATE, SELECTED_TERM } from '../actionTypes/offerActionTypes';
import { SWITCH_VEHICLE } from '../actionTypes/openDealActionTypes';
import { INCLUDE_TRADE_INS_CHANGED } from '../actionTypes/tradesActionTypes';
import { TRADE_IN_PAYMENT_INFO_UPDATE } from '../actionTypes/tradeActionTypes';
import { PRODUCT_CHANGES_PAYMENT_CALL } from '../actionTypes/vehicleProtectionActionTypes';
import {
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    ADD_CATALOG_ACCESSORIES,
    REMOVE_CATALOG_ACCESSORIES
} from '../actionTypes/accessoriesActionTypes';
import { actions as offerReduxActionTypes } from '@makemydeal/dr-offer-redux';

// action creators
import { markAvoFresh, markAvoStale } from '../actions/avoActionCreators';

// selectors
import * as offerReduxSelectors from '../selectors/offerRedux';
import { getIsSoftPull } from '../selectors/deskingSelectors';

// Invalidate on the action itself, not via PAYMENT_RECEIVED: in grid mode a term selection is `{ payment: false }`
// and an offer-type switch routes via program-quotes (no matching PAYMENT_RECEIVED reaches the whitelist below);
// credit-tier / manual-score changes only refresh recs through aiMiddleware's TRIGGER_AI_REFRESH, which is gated to
// finance + primary scope — so marking stale here is what covers lease and non-primary scenarios.
const directInvalidationActionTypes = new Set([
    offerReduxActionTypes.UPDATED_OFFER_TYPE,
    offerReduxActionTypes.SELECTED_CREDIT_TIER,
    offerReduxActionTypes.UPDATED_MANUAL_CREDIT_SCORE,
    SELECTED_TERM
]);

// Payment-recalculated changes that emit a PAYMENT_RECEIVED carrying the trigger — discriminate on
// originalAction. SELECTED_TERM stays here too (idempotent) so a non-grid term change re-invalidates once
// its payment settles, even if a mid-flight refetch already marked AVO fresh; it's also handled directly
// above for grid mode.
const relevantActionTypes = new Set([
    UPDATED_OFFER_PRICE_IMMEDIATE,
    UPDATED_DOWN_PAYMENT_IMMEDIATE,
    SELECTED_TERM,
    SWITCH_VEHICLE,
    TRADE_IN_PAYMENT_INFO_UPDATE,
    INCLUDE_TRADE_INS_CHANGED,
    PRODUCT_CHANGES_PAYMENT_CALL,
    ADDED_MANUAL_ACCESSORIES,
    ADD_ACCESSORIES_SELECTIONS,
    ADD_CATALOG_ACCESSORIES,
    REMOVE_CATALOG_ACCESSORIES
]);

/**
 * AVO Staleness Middleware
 *
 * Marks AVO recommendations stale on a deal change that invalidates them (only when the toggle is on):
 * an offer-type switch, a credit-tier / manual-score change, and a real term change invalidate on the action
 * (grid mode emits no matching PAYMENT_RECEIVED, and credit changes only refresh recs via aiMiddleware on
 * finance + primary scope); a term change ALSO invalidates on its settling PAYMENT_RECEIVED (non-grid), and
 * the remaining payment-recalculated changes invalidate on their PAYMENT_RECEIVED. Marks fresh on
 * AI_VEHICLE_RECOMMENDATIONS_SUCCESS regardless of toggle.
 */
export const middleware: ReduxMiddleware<StateTree> = (store: ReduxStore<StateTree>) => (next: ReduxNext) => (action: AnyFSA) => {
    // SELECTED_TERM fires even when the term doesn't move (grid re-selects the current term on init; flex buy
    // re-applies it), so capture the currently-selected term id before the reducer runs, to compare against
    // the incoming term id (action.payload) below and invalidate only on a real change. getTermIdByOfferType
    // reads the raw id directly (no terms.byId lookup), so it can't collapse to undefined mid-flight.
    const isTermSelection = action?.type === SELECTED_TERM;
    const selectedTermIdBefore = isTermSelection ? offerReduxSelectors.getTermIdByOfferType(store.getState()) : undefined;

    next(action);

    if (!action || !action.type) {
        return;
    }

    if (action.type === AI_VEHICLE_RECOMMENDATIONS_SUCCESS) {
        store.dispatch(markAvoFresh());
        return;
    }

    if (action.type === scopedActionTypes.SCOPES_CHANGE_DEFAULT_SCOPE) {
        if (featureToggleSelectors.isAiVehicleRecommendationsChangeVehicleEnabled(store.getState())) {
            const newPrimaryScopeId = action.payload?.newDefaultScope;
            if (newPrimaryScopeId) {
                store.dispatch(scopedActions.createScopedAction(newPrimaryScopeId, markAvoStale()));
            }
        }
        return;
    }

    const isDirectInvalidation = directInvalidationActionTypes.has(action.type);

    if (!isDirectInvalidation && action.type !== PAYMENT_RECEIVED) {
        return;
    }

    const state = store.getState();

    if (!featureToggleSelectors.isAiVehicleRecommendationsChangeVehicleEnabled(state)) {
        return;
    }

    if (isDirectInvalidation) {
        // Soft pull is the authoritative credit source — a tier / manual-score change is a no-op while it's
        // active (mirrors aiMiddleware), so it must not invalidate AVO and force a needless refetch.
        const isCreditChange =
            action.type === offerReduxActionTypes.SELECTED_CREDIT_TIER ||
            action.type === offerReduxActionTypes.UPDATED_MANUAL_CREDIT_SCORE;
        if (isCreditChange && getIsSoftPull(state)) {
            return;
        }
        // Offer-type always invalidates. Term selection invalidates unless it's a confirmed no-op re-select:
        // the incoming term id (action.payload) equals the previously-selected term id. Comparing the payload
        // (always the real id) avoids depending on the reducer having resolved the new term; if the previous
        // id is unknown, invalidate rather than risk stale recs.
        const isNoOpTermReselect = isTermSelection && selectedTermIdBefore != null && action.payload === selectedTermIdBefore;
        if (isNoOpTermReselect) {
            return;
        }
        store.dispatch(markAvoStale());
        return;
    }

    const originalActionType = action.meta?.originalAction?.type;

    if (relevantActionTypes.has(originalActionType)) {
        store.dispatch(markAvoStale());
    }
};
